import {TreeNodeProps} from '@priority-software/priority-style-react';
import {texts} from '../texts';

enum ReplacementType {
    titleToId,
    idToTitle
}

enum FormatType {
    text = 'text',
    html = 'html'
}

const findIdByTitle = (tree: TreeNodeProps[], title: string = ''): string | null => {
    // id= formTitle::columnTitle
    const splitTitle = title.split('\\', 2);
    const formTitle = splitTitle[0];
    const columnTitle = splitTitle[1];
    for (const node of tree) {
        if (node?.title === columnTitle && node?.extraData?.formTitle === formTitle) {
            return node.id;
        }
        if (node?.nodes) {
            const result = findIdByTitle(node.nodes, title);
            if (result) return result;
        }
    }
    return null;
};

const findTitleById = (tree: TreeNodeProps[], id: string = ''): string | null => {
    for (const node of tree) {
        if (node?.id === id) {
            return node.title;
        }
        if (node?.nodes) {
            const result = findTitleById(node.nodes, id);
            if (result) return result;
        }
    }
    return null;
};

const ignoreRulesTags = (text: string): boolean => {
    const tags = [texts.rule_number, texts.rule_description];
    return tags.some(tag => text.includes(tag));
};

/**
 * This function gets input and if this input has tags in it, the function will change the data according to the format and type.
 * This function is used for clarifying the data as seen by the user in the editor from the data that is in the database.
 * If the user sees <<formTitle::columnTitle>> in the editor, the function will change it to <<formName.columnName>> for savinge in the server and vice versa.
 * @example For: input: <<ORDERS.CUSTNAME>>, type: idToTitle, format: text; the output will be <<Sale orders::Customer Name>>
 * @example For: input: <<Sale orders::Customer Name>>, type: titleToId, format: text; the output will be <<ORDERS.CUSTNAME>>
 */

const formats = {
    html: {
        openTag: '&lt;&lt;',
        closeTag: '&gt;&gt;',
        regex: /&lt;&lt;(.*?)&gt;&gt;/g // non-greedy match
    },
    text: {
        openTag: '<<',
        closeTag: '>>',
        regex: /<<(.*?)>>/g
    }
};

const replaceTagsData = (
    input: string = '',
    treeValues: TreeNodeProps,
    type: ReplacementType,
    inputFormat: FormatType,
    outputFormat: FormatType
): string => {
    if (!input) return input;
    /**
     * tags logic: the server should always get the data in double less than greater then signs : <<data>>
     * the client side should change it depend on the way of the data is displayed.
     * if the data is the subject than it displayed as <<data>>
     * if the data is in the body (html editor) than it displayed as &lt;&lt;data&gt;&gt;
     */

    const inFmt = formats[inputFormat];
    const outFmt = formats[outputFormat];

    return input.replace(inFmt.regex, (_: string, id: string) => {
        if (ignoreRulesTags(id)) return `${outFmt.openTag}${id}${outFmt.closeTag}`;

        if (type === ReplacementType.titleToId) {
            const result = findIdByTitle([treeValues], id);
            return `${outFmt.openTag}${result ?? id}${outFmt.closeTag}`;
        } else {
            const [formId] = id.split('.', 2);
            const formTitle = findTitleById([treeValues], formId);
            const columnTitle = findTitleById([treeValues], id);
            const result = formTitle ? `${formTitle}\\${columnTitle}` : id;
            return `${outFmt.openTag}${result}${outFmt.closeTag}`;
        }
    });
};

export const replaceTitlesWithIds = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, ReplacementType.titleToId, FormatType.text, FormatType.text);

export const replaceIdsWithTitles = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, ReplacementType.idToTitle, FormatType.text, FormatType.text);

export const replaceTitlesWithIdsForHtml = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, ReplacementType.titleToId, FormatType.html, FormatType.text);

export const replaceIdsWithTitlesForHtml = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, ReplacementType.idToTitle, FormatType.text, FormatType.html);
